import type * as React from 'react';

import { table } from '@gitbook/doc-core';

import { isModShiftSlash } from '../../../common';
import { definitionSpecs } from '../../ColumnDefinitions/definitions';
import { useCellButtons } from '../../ColumnDefinitions/hooks/useCellButtons';
import type { TableRecordValueProps } from '../../ColumnDefinitions/types';
import { useTablePlugin } from '../../plugin';

/**
 * Display the value that a table record has within a given column.
 * In human: show the value of a cell in a table.
 */
export function TableRecordValue(props: TableRecordValueProps): React.ReactElement {
    const {
        record,
        columnDefinition,
        readOnly,
        editor,
        nodeKey,
        selected,
        view,
        definitionColorKind,
        diff,
        onPressRowOptions,
    } = props;

    const { ValueComponent } = definitionSpecs[props.columnDefinition.type];
    const [tablePlugin] = useTablePlugin();
    const onValueChange = async (value) => {
        editor.change((change) => {
            table.setRecordValue(change, nodeKey, record.key, columnDefinition.id, value);
        });

        // Maintain selection after inserting value, otherwise the selection gets lost when inserting values through palette's and dialogs (e.g. in Files, Users, Select).
        editor.change((change) => {
            table.moveTableSelection(change, nodeKey, {
                recordId: record.key,
                definitionId: columnDefinition.id,
            });
        });
    };

    const onSelectionChange = (focused: boolean) => {
        if (focused) {
            editor.change((change) => {
                table.moveTableSelection(change, nodeKey, {
                    recordId: record.key,
                    definitionId: columnDefinition.id,
                });
            });
        }
    };

    // Handle keydown in non-Slate node, and have them also trigger the table plugin's onKeyDown handler. Slate nodes (like a Text cell) are handled through the plugins, but non-Slate nodes like most other cells should also be evaluated.
    const handleNonSlateKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        // Handle opening the row options palette shortcut
        if (isModShiftSlash(event)) {
            event.preventDefault();
            onPressRowOptions && selected && onPressRowOptions();
            return;
        }

        event.preventDefault();

        // Execute the same onKeyDown handler as the table plugin, only on non-Slate nodes
        if (tablePlugin.onKeyDown) {
            // Create a change object to pass to the onKeyDown handler
            const change = editor.value.change();
            tablePlugin.onKeyDown(event as unknown as KeyboardEvent, change, editor);

            editor.onChange(change);
        }
    };

    // Comment and cell options (palette) buttons
    const tableCellButton = useCellButtons({
        nodeKey,
        editor,
        selected,
        readOnly,
        definition: columnDefinition,
        recordKey: record.key,
        definitionColorKind,
        view,
        value: record.value.values[columnDefinition.id],
        onValueChange,
        onKeyDown: handleNonSlateKeyDown,
    });

    const diffView = diff?.[record.key]?.[columnDefinition.id];
    const PrevValueComponent =
        typeof diffView?.before !== 'undefined'
            ? definitionSpecs[diffView.before.definition.type].ValueComponent
            : null;

    return (
        <div
            className="relative flex flex-1"
            onClick={(event) => {
                if (columnDefinition.type === 'text') {
                    return;
                }

                // We prevent the event from being handled by the editor and moving focus out of the input
                event.stopPropagation();
                onSelectionChange(true);
            }}
            tabIndex={-1}
        >
            {diffView ? (
                <div className="flex w-fit flex-1 flex-col gap-1">
                    {PrevValueComponent && typeof diffView.before !== 'undefined' ? (
                        <div className="bg-danger-standout p-1">
                            <PrevValueComponent
                                editor={editor}
                                nodeKey={nodeKey}
                                readOnly={readOnly}
                                displayMode="cell"
                                selected={selected}
                                value={diffView.before.value}
                                definitionColorKind={definitionColorKind}
                                definition={diffView.before.definition}
                                onValueChange={onValueChange}
                                onSelectionChange={onSelectionChange}
                                view={view}
                                onKeyDown={handleNonSlateKeyDown}
                            />
                        </div>
                    ) : null}
                    <div className="bg-success-standout p-1">
                        <ValueComponent
                            editor={editor}
                            nodeKey={nodeKey}
                            readOnly={readOnly}
                            displayMode="cell"
                            selected={selected}
                            value={diffView.after}
                            definitionColorKind={definitionColorKind}
                            definition={columnDefinition}
                            onValueChange={onValueChange}
                            onSelectionChange={onSelectionChange}
                            view={view}
                            onKeyDown={handleNonSlateKeyDown}
                        />
                    </div>
                </div>
            ) : (
                <ValueComponent
                    editor={editor}
                    nodeKey={nodeKey}
                    readOnly={readOnly}
                    displayMode="cell"
                    selected={selected}
                    value={record.value.values[columnDefinition.id]}
                    definitionColorKind={definitionColorKind}
                    definition={columnDefinition}
                    onValueChange={onValueChange}
                    onSelectionChange={onSelectionChange}
                    view={view}
                    onKeyDown={handleNonSlateKeyDown}
                />
            )}
            {tableCellButton}
        </div>
    );
}
